<template>
  <el-form ref="form">
    <div class="table-contract-container">
      <el-row>
        <el-col :span="6" class="filter-input">
          <el-input
            :placeholder="$t('keyword')"
            @input="isSearching = true"
            v-model="keyword"></el-input>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="24" v-loading="isSearching">
          <div class="contract-table contract-table-head">
            <ul>
              <li style="width: 5%"></li>
              <li style="width: 25%">{{$t('name')}}</li>
              <li style="width: 20%; text-align: right">{{$t('price')}}</li>
              <li style="width: 15%; text-align: center">{{$t('pax')}}</li>
              <li style="width: 35%">{{$t('conditionDetail')}}</li>
            </ul>
          </div>
          <div v-for="(transfer, index) of transferTableDataList" :key="`transfer-${index}`">
            <div class="contract-table" v-show="transfer.isShow">
              <ul>
                <li style="width: 5%; text-align: center"><el-checkbox v-model="transferCheckList[index].isCheck" @change="onSelectedTransferChange"></el-checkbox></li>
                <li style="width: 25%">{{transfer.name}}</li>
                <li style="width: 20%; text-align: right">{{ bindPrice(transfer) }}</li>
                <li style="width: 15%; text-align: center">
                  <div>Min: {{ transfer.minPax }}</div>
                  <div>Max: {{ transfer.maxPax }}</div>
                </li>
                <li style="width: 35%">{{ getConditionDetail(transfer) }}</li>
              </ul>
              <div class="contract-data-version" v-if="isUpdatedData(transferList, transfer)">
                <div class="contract-data-updated">
                  <div style="padding: 20px 0 0 20px">
                    <strong>*{{$t('updatedTransferMessage')}}</strong>
                  </div>
                  <el-radio-group style="padding-left: 40px" v-model="transferCheckList[index].version" fill="#ff0000" @change="onSelectedTransferChange">
                    <div class="contract-data-version-radio">
                      <el-radio :label="DATA_VERSION.SELECTED">{{$t('selectedVersion')}} ({{bindUpdatedAt(transfer.updatedAt)}})</el-radio>
                    </div>
                    <div class="contract-data-version-radio">
                      <el-radio :label="DATA_VERSION.LATEST">{{$t('latestVersion')}} ({{bindLatestUpdatedAt(transfer.id)}})</el-radio>
                    </div>
                  </el-radio-group>
                </div>
              </div>
              <div class="contract-data-version" v-if="isDeletedData(transferList, transfer.id)">
                <div class="contract-data-deleted">
                  <div style="padding: 20px 0 0 20px">
                    <strong style="color: #dd3333">*{{$t('deletedTransferMessage')}}</strong>
                  </div>
                  <el-radio-group style="padding-left: 40px" v-model="transfer.id" fill="#ff0000" >
                    <el-radio :label="transfer.id" class="contract-data-version-radio">{{$t('selectedVersion')}} ({{bindUpdatedAt(transfer.updatedAt)}})</el-radio>
                  </el-radio-group>
                </div>
              </div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
    <remark-component
      :remark-type="REMARK_TYPE.TRANSFER"
      :remark-list="defaultRemarkList"
      :contract-detail="contractDetail"
      @onRemarkChange="onRemarkChange">
    </remark-component>
  </el-form>
</template>

<script>
import { TRANSFER_TYPE, REMARK_TYPE } from '@/configs/constant'
import i18nMessages from '@/translations/Contract/components/TabContractTransfer.i18n'
import RemarkComponent from '@/views/Contract/components/RemarkComponent'
import { numberFormat, bindData } from '@/helpers'
import { filterDataByKeyword } from '@/services/general-service'
import { getTableDataSource } from '@/services/contract-service'
import { get, debounce } from 'lodash'
import { applyNewRemark } from '@/domains/contract-domain'
import { dateFormat } from '@/helpers/datetime'
import {
  isUpdatedData,
  isDeletedData,
  DATA_VERSION,
  getSelectedDataListByVersion,
  getDataCheckList
} from '@/services/version-service'

export default {
  name: 'TabTransferComponent',
  components: { RemarkComponent },
  i18n: { messages: i18nMessages },
  model: {
    prop: 'contractDetail',
    event: 'onTransferDetailChange'
  },
  props: {
    defaultRemarkList: {
      type: Array,
      default: function () {
        return []
      }
    },
    transferList: {
      type: Array,
      default: function () {
        return []
      }
    },
    contractDetail: {
      type: Object,
      default: function () {
        return {}
      }
    }
  },
  watch: {
    keyword: debounce(function () {
      this.isSearching = false
    }, 300),
    isSearching: function (value) {
      if (!value) {
        this._filterTransferListDataSource()
      }
    }
  },
  data () {
    return {
      REMARK_TYPE,
      DATA_VERSION,
      isUpdatedData,
      isDeletedData,
      transferTableDataList: [],
      transferCheckList: [],
      newRemark: {},
      keyword: '',
      isSearching: false
    }
  },
  created: function () {
    this._getTransferListDataSource()

    const transferList = get(this.contractDetail, 'transferList', [])
    this.transferCheckList = getDataCheckList(this.transferTableDataList, transferList)
  },
  methods: {
    bindPrice: function (row) {
      const suffix = '.-'
      const unitType = this.getTransferUnitType(row.unitType)
      const price = numberFormat(row.price, suffix)
      return `${price} ${unitType}`
    },
    getTransferUnitType: function (unitType) {
      switch (unitType) {
        case TRANSFER_TYPE.PER_CAR:
          return this.$t('transferUnitTypeCar')
        case TRANSFER_TYPE.PER_PERSON:
          return this.$t('transferUnitTypePerPerson')
        default:
          return 'N/A'
      }
    },
    getConditionDetail: function (row) {
      return bindData(row, 'conditionDetail')
    },
    bindLatestUpdatedAt: function (contractTransferId) {
      const transfer = this.transferList.find(item => item.id === contractTransferId)

      if (transfer !== undefined) {
        const date = new Date(transfer.updatedAt)
        return dateFormat.long(date)
      }
    },
    bindUpdatedAt: function (updatedDate) {
      const date = new Date(updatedDate)
      return dateFormat.long(date)
    },
    onSelectedTransferChange () {
      const remarkList = applyNewRemark(this.contractDetail, this.newRemark)
      const result = getSelectedDataListByVersion(
        this.transferCheckList,
        this.transferTableDataList,
        this.transferList
      )
      const transferList = {
        transferList: result
      }

      const newRemarkList = {
        remarkList: remarkList
      }

      const contractDetail = Object.assign({}, this.contractDetail, transferList, newRemarkList)
      this.$emit('onTransferDetailChange', contractDetail)
    },
    onRemarkChange: function (newRemark) {
      this.newRemark = newRemark

      this.onSelectedTransferChange()
    },
    _getTransferListDataSource: function () {
      const transferList = get(this.contractDetail, 'transferList', [])
      this.transferTableDataList = getTableDataSource(this.transferList, transferList)
      this.transferTableDataList.sort((a, b) => (a.name > b.name) ? 1 : -1)

      this._prepareFilter()
    },
    _prepareFilter: function () {
      this.transferTableDataList = this.transferTableDataList.map((transfer) => {
        transfer.isShow = true
        return transfer
      })
    },
    _filterTransferListDataSource: function () {
      const searchKeys = ['name']
      const filteredTransferDataList = filterDataByKeyword(this.transferTableDataList, this.keyword, searchKeys)

      this.transferTableDataList = this.transferTableDataList.map((transfer) => {
        const filteredTransfer = filteredTransferDataList.find(item => item.id === transfer.id)
        transfer.isShow = (filteredTransfer !== undefined)

        return transfer
      })
    }
  }
}
</script>

<style scoped>
  .filter-input {
    left: 5%;
    position: relative;
    padding: 10px 0;
  }
</style>
